import React from 'react';

import { isNil } from 'ramda';
import { Control, FormState, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AUX_TRANSLATIONS } from 'RestAPIs/endpoints';
import { useDeleteById } from 'RestAPIs/useDeleteById';

import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import { ControlledSelect } from 'components/shared';
import TextField from 'components/shared/ControlledFields/ControlledTextField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import RemoveButton from 'components/shared/RemoveButton';

import { AuxFormValues } from './AuxValuesForm';

interface TranslationSectionProps {
  control: Control<AuxFormValues>;
  formState: FormState<AuxFormValues>;
}

const languageOptions = [
  {
    label: 'NL',
    value: 'nl'
  },
  {
    label: 'EN',
    value: 'en'
  },
  {
    label: 'DE',
    value: 'de'
  }
];

const TranslationSection: React.FC<TranslationSectionProps> = ({
  control,
  formState
}) => {
  const { t } = useTranslation('common');

  const { fields, append, remove } = useFieldArray<AuxFormValues>({
    name: 'translations'
  });

  const addTranslationField = () => {
    append({
      languageCode: '',
      name: ''
    });
  };

  const { mutateAsync: deleteAuxTranslation } = useDeleteById(AUX_TRANSLATIONS);

  const removeTranslationField = async (field, index) => {
    if (field.auxValueTranslationId) {
      await deleteAuxTranslation(field.auxValueTranslationId).then(() =>
        remove(index)
      );
    } else {
      remove(index);
    }
  };

  return (
    <MultiStepForm.Section
      title={t('auxValues.formSectionLabels.translations')}
      validateFields={[]}
    >
      {!isNil(fields) &&
        fields.map((field, index) => (
          <div key={field.id} style={{ position: 'relative' }}>
            <RemoveButton
              onClick={() => removeTranslationField(field, index)}
            />
            <Box display="flex" gap={2.5}>
              <Box sx={{ width: 1 / 3 }}>
                <ControlledSelect
                  name={`translations.${index}.languageCode`}
                  control={control}
                  variant="outlined"
                  label={t('auxValues.group')}
                  options={languageOptions}
                />
              </Box>
              <Box sx={{ width: 2 / 3 }}>
                <TextField
                  name={`translations.${index}.name`}
                  control={control}
                  variant="outlined"
                  label={t('auxValues.code')}
                />
              </Box>
            </Box>
          </div>
        ))}
      {formState.errors?.translations && (
        <Typography color={'red'}>
          {formState.errors?.translations.message}
        </Typography>
      )}

      <Button
        color="primary"
        variant="outlined"
        onClick={addTranslationField}
        style={{ alignSelf: 'end' }}
      >
        {t('addMore')}
      </Button>
    </MultiStepForm.Section>
  );
};

export default TranslationSection;
